import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { notificar } from '../utils/notify';
import '../styles/bporh_admissoes.css';

// Sindicatos — convenção coletiva de cada cliente e perguntas à IA.
//
// O operador escolhe o cliente, confere o sindicato/data-base, sobe a CCT (e
// aditivos) em PDF e pergunta em linguagem natural ("qual o piso?", "quantos
// dias de licença por casamento?"). A resposta vem do Gemini lendo SÓ as
// convenções daquele cliente, com a cláusula citada. Cada cliente é um
// contexto isolado: nada de um vaza para outro.

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

const TIPO_DOC = {
  cct: { rotulo: 'CCT', cor: '#0e6e86', bg: '#e3f6fb' },
  aditivo: { rotulo: 'Aditivo', cor: '#7a3b9e', bg: '#f3e8fb' },
  acordo: { rotulo: 'Acordo', cor: '#116b47', bg: '#e6f4ee' },
  outro: { rotulo: 'Documento', cor: '#6c757d', bg: '#f1f3f5' },
};

const fmtMB = (b) => (b ? `${(b / (1024 * 1024)).toFixed(1)} MB` : '');

// Markdown mínimo da resposta da IA: títulos, listas, negrito. Sem lib externa
// e sem HTML cru — o texto vem de fora, então vira nó React, não innerHTML.
const negrito = (linha) => {
  const partes = linha.split(/(\*\*[^*]+\*\*)/g);
  return partes.map((p, i) => (p.startsWith('**') && p.endsWith('**')
    ? <b key={i}>{p.slice(2, -2)}</b> : p));
};
const Markdown = ({ texto }) => {
  const linhas = String(texto || '').replace(/\r/g, '').split('\n');
  const blocos = [];
  let lista = null;
  const fechaLista = () => { if (lista) { blocos.push(lista); lista = null; } };
  linhas.forEach((l, i) => {
    const m = l.match(/^\s*(?:[-*•]|\d+[.)])\s+(.*)$/);
    if (m) {
      if (!lista) lista = { tipo: /^\s*\d/.test(l) ? 'ol' : 'ul', itens: [], key: `l${i}` };
      lista.itens.push(m[1]);
      return;
    }
    fechaLista();
    if (!l.trim()) return;
    const h = l.match(/^\s*#{1,4}\s+(.*)$/);
    if (h) blocos.push({ tipo: 'h', texto: h[1], key: `h${i}` });
    else blocos.push({ tipo: 'p', texto: l, key: `p${i}` });
  });
  fechaLista();
  return (
    <div className="resposta-ia">
      {blocos.map((b) => {
        if (b.tipo === 'h') return <div key={b.key} className="fw-bold mt-2 mb-1">{negrito(b.texto)}</div>;
        if (b.tipo === 'p') return <p key={b.key} className="mb-2">{negrito(b.texto)}</p>;
        const Tag = b.tipo;
        return (
          <Tag key={b.key} className="mb-2 ps-4">
            {b.itens.map((it, j) => <li key={j}>{negrito(it)}</li>)}
          </Tag>
        );
      })}
    </div>
  );
};

const BpoRhSindicatos = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const clienteId = searchParams.get('cliente') || '';
  const setClienteId = (v) => setSearchParams((p) => {
    const n = new URLSearchParams(p);
    if (v) n.set('cliente', String(v)); else n.delete('cliente');
    return n;
  });

  const [dados, setDados] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  useEffect(() => { if (mensagem) notificar(mensagem); }, [mensagem]);

  // cadastro do sindicato
  const [editSind, setEditSind] = useState(null);   // {nome, data_base, obs}
  const [salvandoSind, setSalvandoSind] = useState(false);

  // upload
  const [enviando, setEnviando] = useState(false);
  const inputArquivo = useRef(null);

  // perguntas
  const [pergunta, setPergunta] = useState('');
  const [docFiltro, setDocFiltro] = useState('');
  const [perguntando, setPerguntando] = useState(false);
  const [confirmaExcluir, setConfirmaExcluir] = useState(null);   // {tipo, id, nome}

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(`${API}/api/bporh/sindicatos/`,
      { params: clienteId ? { cliente_id: clienteId } : {}, headers: headers() })
      .then((res) => {
        setClientes(res.data.clientes || []);
        setDados(clienteId ? res.data : null);
      })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível carregar'}`))
      .finally(() => setCarregando(false));
  }, [clienteId]);
  useEffect(() => { carregar(); setEditSind(null); setDocFiltro(''); }, [carregar]);

  const clienteAtual = clientes.find((c) => String(c.id) === String(clienteId));

  const salvarSindicato = () => {
    if (!editSind) return;
    setSalvandoSind(true);
    axios.post(`${API}/api/bporh/sindicatos/`,
      { cliente_id: Number(clienteId), nome: editSind.nome, data_base: editSind.data_base || null,
        obs: editSind.obs },
      { headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); setEditSind(null); carregar(); })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível salvar'}`))
      .finally(() => setSalvandoSind(false));
  };

  const enviarPdf = (arquivo) => {
    if (!arquivo) return;
    const fd = new FormData();
    fd.append('cliente_id', clienteId);
    fd.append('arquivo', arquivo);
    setEnviando(true);
    axios.post(`${API}/api/bporh/sindicatos/`, fd, { headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); carregar(); })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível importar o PDF'}`))
      .finally(() => { setEnviando(false); if (inputArquivo.current) inputArquivo.current.value = ''; });
  };

  // scan cuja transcrição falhou: tenta de novo (leva ~1 min por 20 páginas)
  const [extraindo, setExtraindo] = useState(null);
  const extrairTexto = (d) => {
    setExtraindo(d.id);
    axios.post(`${API}/api/bporh/sindicatos/`,
      { cliente_id: Number(clienteId), documento_id: d.id, ocr: true }, { headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); carregar(); })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível extrair o texto'}`))
      .finally(() => setExtraindo(null));
  };

  const abrirPdf = (d) => {
    axios.get(`${API}/api/bporh/sindicatos/documento/${d.id}/`,
      { params: { cliente_id: clienteId }, headers: headers(), responseType: 'blob' })
      .then((res) => window.open(URL.createObjectURL(res.data), '_blank'))
      .catch(() => setMensagem('❌ Não foi possível abrir o PDF'));
  };

  const excluir = () => {
    if (!confirmaExcluir) return;
    const params = { cliente_id: clienteId,
      ...(confirmaExcluir.tipo === 'doc' ? { id: confirmaExcluir.id } : { pergunta_id: confirmaExcluir.id }) };
    axios.delete(`${API}/api/bporh/sindicatos/`, { params, headers: headers() })
      .then((res) => { setMensagem(`✅ ${res.data.mensagem}`); carregar(); })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'Não foi possível remover'}`))
      .finally(() => setConfirmaExcluir(null));
  };

  const perguntar = () => {
    const p = pergunta.trim();
    if (!p || perguntando) return;
    setPerguntando(true);
    axios.post(`${API}/api/bporh/sindicatos/perguntar/`,
      { cliente_id: Number(clienteId), pergunta: p, documento_id: docFiltro || null },
      { headers: headers() })
      .then((res) => {
        setPergunta('');
        // a resposta entra no topo do histórico sem recarregar a página
        setDados((d) => (d ? { ...d, perguntas: [res.data.item, ...(d.perguntas || [])] } : d));
      })
      .catch((err) => setMensagem(`❌ ${err.response?.data?.mensagem || 'A IA não conseguiu responder'}`))
      .finally(() => setPerguntando(false));
  };

  const docs = dados?.documentos || [];
  const perguntas = dados?.perguntas || [];
  const sind = dados?.sindicato;
  const semTexto = docs.filter((d) => !d.tem_texto).length;

  return (
    <div className="bporh-admissoes-page">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-handshake header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">
              BPO RH — Sindicatos
              {clienteAtual && <span className="text-muted"> · {clienteAtual.cliente}</span>}
            </h2>
          </div>
          <button className="btn btn-outline-primary btn-sm" onClick={carregar} disabled={carregando}>
            <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'} me-2`}></i>Atualizar
          </button>
        </div>

        <div className="alert alert-light border small mb-3">
          <i className="fas fa-info-circle me-2 text-primary"></i>
          Escolha o cliente, confira o <b>sindicato e a data-base</b>, importe a <b>convenção
          coletiva</b> (e aditivos) em PDF e pergunte em português: piso, reajuste, VR, licenças,
          contribuições. A IA responde lendo <b>apenas as convenções deste cliente</b> e cita a
          cláusula. A resposta é insumo de trabalho — para tema controverso, confira no PDF.
        </div>

        <div className="row g-2 mb-3">
          <div className="col-md-4">
            <label className="form-label small fw-bold mb-1 text-muted">Cliente</label>
            <select className="form-select form-select-sm" value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}>
              <option value="">Selecione o cliente...</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.cliente}{c.documentos ? ` · ${c.documentos} doc${c.documentos === 1 ? '' : 's'}` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {!clienteId ? (
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>Cliente</th><th>Sindicato</th><th>Data-base</th>
                  <th className="text-center">Convenções</th><th>Operadora</th>
                </tr>
              </thead>
              <tbody>
                {clientes.map((c) => (
                  <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => setClienteId(c.id)}>
                    <td className="fw-bold">{c.cliente}</td>
                    <td className="small">{c.sindicato || <span className="text-muted">não informado</span>}</td>
                    <td className="small">{c.data_base ? c.data_base.slice(0, 5) : '—'}</td>
                    <td className="text-center">
                      {c.documentos ? <span className="badge bg-success">{c.documentos}</span>
                        : <span className="badge bg-light text-muted border">nenhuma</span>}
                    </td>
                    <td className="small text-muted">{c.operador || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="row g-3">
            {/* ─── coluna esquerda: sindicato + documentos ─── */}
            <div className="col-lg-5">
              <div className="bg-white rounded shadow-sm p-3 mb-3" style={{ borderLeft: '4px solid #00b5cc' }}>
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div>
                    <div className="small text-muted fw-bold">SINDICATO DA CATEGORIA</div>
                    {editSind ? null : (
                      <>
                        <div className="fw-semibold">{sind?.nome || <span className="text-muted">não informado</span>}</div>
                        <div className="small text-muted mt-1">
                          Data-base: <b>{sind?.data_base_br || '—'}</b>
                          {sind?.obs && <> · {sind.obs}</>}
                        </div>
                      </>
                    )}
                  </div>
                  {!editSind && (
                    <button className="btn btn-sm btn-light border"
                      onClick={() => setEditSind({ nome: sind?.nome || '', data_base: sind?.data_base || '', obs: sind?.obs || '' })}>
                      <i className="fas fa-pen me-1"></i>{sind?.nome ? 'alterar' : 'cadastrar'}
                    </button>
                  )}
                </div>
                {editSind && (
                  <div className="mt-2">
                    <input type="text" className="form-control form-control-sm mb-2" autoFocus
                      placeholder="Nome do sindicato" value={editSind.nome}
                      onChange={(e) => setEditSind((s) => ({ ...s, nome: e.target.value }))} />
                    <div className="d-flex gap-2 mb-2">
                      <div>
                        <label className="form-label small mb-0">Data-base</label>
                        <input type="date" className="form-control form-control-sm" value={editSind.data_base}
                          onChange={(e) => setEditSind((s) => ({ ...s, data_base: e.target.value }))} />
                      </div>
                      <div className="flex-grow-1">
                        <label className="form-label small mb-0">Observação</label>
                        <input type="text" className="form-control form-control-sm" value={editSind.obs}
                          placeholder="ex.: patronal Sinapro-SP"
                          onChange={(e) => setEditSind((s) => ({ ...s, obs: e.target.value }))} />
                      </div>
                    </div>
                    <div className="d-flex gap-2 justify-content-end">
                      <button className="btn btn-sm btn-secondary" onClick={() => setEditSind(null)} disabled={salvandoSind}>Cancelar</button>
                      <button className="btn btn-sm btn-primary" onClick={salvarSindicato} disabled={salvandoSind}>
                        <i className={`fas ${salvandoSind ? 'fa-spinner fa-spin' : 'fa-save'} me-1`}></i>Salvar
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white rounded shadow-sm p-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="fw-bold mb-0"><i className="fas fa-file-contract me-2 text-muted"></i>Convenções importadas</h6>
                  <label className={`btn btn-sm btn-primary mb-0 ${enviando ? 'disabled' : ''}`}
                    title={`PDF de até ${dados?.pdf_maximo_mb || 25}MB. O texto é extraído e guardado; o arquivo fica no armazenamento privado.`}>
                    <i className={`fas ${enviando ? 'fa-spinner fa-spin' : 'fa-file-arrow-up'} me-1`}></i>
                    {enviando ? 'Importando...' : 'Importar PDF'}
                    <input ref={inputArquivo} type="file" accept="application/pdf" hidden disabled={enviando}
                      onChange={(e) => enviarPdf(e.target.files?.[0])} />
                  </label>
                </div>
                {enviando && (
                  <div className="small text-muted mb-2">
                    <i className="fas fa-circle-info me-1"></i>Lendo o PDF e identificando título e vigência. Se for digitalizado, a IA transcreve o texto — cerca de 1 minuto a cada 20 páginas.
                  </div>
                )}
                {docs.length === 0 ? (
                  <div className="text-muted small py-3 text-center">
                    Nenhuma convenção importada. Suba o PDF da CCT vigente para começar a perguntar.
                  </div>
                ) : docs.map((d) => {
                  const t = TIPO_DOC[d.tipo] || TIPO_DOC.outro;
                  return (
                    <div key={d.id} className="border rounded p-2 mb-2">
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div style={{ minWidth: 0 }}>
                          <span className="badge me-2" style={{ background: t.bg, color: t.cor, border: `1px solid ${t.cor}` }}>{t.rotulo}</span>
                          <span className="fw-semibold">{d.titulo}</span>
                          <div className="small text-muted text-truncate" title={d.nome}>
                            {d.nome}{d.paginas ? ` · ${d.paginas} pág.` : ''}{d.tamanho ? ` · ${fmtMB(d.tamanho)}` : ''}
                          </div>
                          <div className="small mt-1">
                            {d.vigencia_inicio || d.vigencia_fim ? (
                              <span className={d.vigente === false ? 'text-danger' : 'text-muted'}>
                                <i className="far fa-calendar me-1"></i>
                                vigência {d.vigencia_inicio || '?'} a {d.vigencia_fim || '?'}
                                {d.vigente === false && ' · vencida'}
                              </span>
                            ) : <span className="text-muted">vigência não identificada</span>}
                            {!d.tem_texto && (
                              <button className="btn btn-sm btn-warning py-0 ms-2" disabled={extraindo === d.id}
                                onClick={() => extrairTexto(d)}
                                title="PDF digitalizado sem texto: a IA transcreve o documento (leva cerca de 1 minuto a cada 20 páginas)">
                                <i className={`fas ${extraindo === d.id ? 'fa-spinner fa-spin' : 'fa-text-width'} me-1`}></i>
                                {extraindo === d.id ? 'Transcrevendo...' : 'Extrair texto'}
                              </button>
                            )}
                          </div>
                          {d.resumo && <div className="small mt-1" style={{ color: '#495057' }}>{d.resumo}</div>}
                          <div className="small text-muted mt-1">por {d.enviado_por || '—'} em {d.em}</div>
                        </div>
                        <div className="d-flex flex-column gap-1">
                          <button className="btn btn-sm btn-light border" onClick={() => abrirPdf(d)}
                            disabled={!d.no_bucket} title={d.no_bucket ? 'Abrir o PDF' : 'PDF não está no armazenamento'}>
                            <i className="fas fa-file-pdf text-danger"></i>
                          </button>
                          <button className="btn btn-sm btn-light border"
                            onClick={() => setConfirmaExcluir({ tipo: 'doc', id: d.id, nome: d.titulo })} title="Remover">
                            <i className="fas fa-trash text-muted"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ─── coluna direita: perguntas ─── */}
            <div className="col-lg-7">
              <div className="bg-white rounded shadow-sm p-3">
                <h6 className="fw-bold mb-2"><i className="fas fa-comments me-2 text-muted"></i>Pergunte à convenção</h6>
                <textarea className="form-control mb-2" rows={3} value={pergunta}
                  disabled={!docs.length || perguntando}
                  placeholder={docs.length
                    ? 'Ex.: Qual o piso salarial? Quantos dias de licença por casamento? Como funciona o vale-refeição?'
                    : 'Importe uma convenção para perguntar'}
                  onChange={(e) => setPergunta(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) perguntar(); }} />
                <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
                  <select className="form-select form-select-sm" style={{ maxWidth: 320 }}
                    value={docFiltro} onChange={(e) => setDocFiltro(e.target.value)} disabled={docs.length < 2}
                    title="Por padrão a IA lê todas as convenções e aditivos do cliente">
                    <option value="">Todos os documentos do cliente</option>
                    {docs.map((d) => <option key={d.id} value={d.id}>{d.titulo}</option>)}
                  </select>
                  <button className="btn btn-primary btn-sm" onClick={perguntar}
                    disabled={!pergunta.trim() || !docs.length || perguntando} title="Ctrl+Enter">
                    <i className={`fas ${perguntando ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>
                    {perguntando ? 'Consultando...' : 'Perguntar'}
                  </button>
                </div>
                {semTexto > 0 && (
                  <div className="small text-muted mt-2">
                    <i className="fas fa-triangle-exclamation me-1 text-warning"></i>
                    {semTexto} documento{semTexto === 1 ? '' : 's'} sem texto: a IA não consegue ler — use <b>Extrair texto</b> no documento.
                  </div>
                )}

                <hr />
                {perguntas.length === 0 ? (
                  <div className="text-muted small text-center py-3">Nenhuma pergunta feita ainda para este cliente.</div>
                ) : perguntas.map((p) => (
                  <div key={p.id} className="mb-3">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div className="fw-semibold" style={{ color: '#212529' }}>
                        <i className="fas fa-circle-question me-2 text-primary"></i>{p.pergunta}
                      </div>
                      <button className="btn btn-sm btn-link text-muted p-0" title="Remover do histórico"
                        onClick={() => setConfirmaExcluir({ tipo: 'pergunta', id: p.id, nome: p.pergunta })}>
                        <i className="fas fa-xmark"></i>
                      </button>
                    </div>
                    <div className="rounded p-3 mt-1" style={{ background: '#f8f9fa', borderLeft: '3px solid #00b5cc' }}>
                      <Markdown texto={p.resposta} />
                      <div className="small text-muted mt-2">
                        {p.autor ? `${p.autor} · ` : ''}{p.em}
                        {p.documentos?.length ? ` · fonte: ${p.documentos.map((d) => d.titulo).join(', ')}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {confirmaExcluir && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 460 }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-trash me-2"></i>Remover {confirmaExcluir.tipo === 'doc' ? 'documento' : 'pergunta'}
                  </h5>
                  <small style={{ color: '#6c757d' }}>{confirmaExcluir.nome}</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setConfirmaExcluir(null)}></button>
              </div>
              <div className="modal-body small">
                {confirmaExcluir.tipo === 'doc'
                  ? 'O PDF e o texto extraído serão apagados. As perguntas já respondidas ficam no histórico.'
                  : 'A pergunta e a resposta saem do histórico deste cliente.'}
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-secondary" onClick={() => setConfirmaExcluir(null)}>Cancelar</button>
                <button className="btn btn-danger" onClick={excluir}><i className="fas fa-trash me-2"></i>Remover</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BpoRhSindicatos;
